import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENCustonWrenchIcon from "@tCentro/Icons/CENCustonWrenchIcon";
import ProductListActions from "@tCentro/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@tCentro/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@tCentro/Overrides/Widgets/ProductList/ProductListProductInformation";
import { COLORS, NISCO, TYPOGRAPHY } from "@tCentro/theme/design";
import CENProductListProductCompare from "@tCentro/Widgets/Product/CENProductListProductCompare";
import NISProductListProductInformation from "@tCentro/Widgets/ProductList/NISProductListProductInformation";
import React, { FC } from "react";
import { css } from "styled-components";
import NISProductListProductCompare from "@tCentro/Widgets/ProductList/NISProductListProductCompare";
import NISProductListProductImage from "@tCentro/Widgets/ProductList/NISProductListProductImage";
import NISCustonWrenchIcon from "@tCentro/Icons/NISCustonWrenchIcon";
import NISProductListActions from "@tCentro/Widgets/ProductList/NISProductListActions";

type Props = ProductCardSelections & HasProduct;

export interface NISProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    compareWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    imageProductWrapper?: InjectableCss;
    moreOptionsStyles?: InjectableCss;
    wrenchIconStyles?: IconPresentationProps;
    configureProductPlpStyles?: InjectableCss;
}

export const nisProductGridCardStyles: NISProductListProductGridCardStyles = {
    moreOptionsStyles: {
        css: css`
            margin-bottom: 8px;
            border: 2px solid ${NISCO.COLORS.primaryColor};
            padding: 4px 12px 4px 8px;
            border-radius: 16px;
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
            span {
                font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
                font-size: 12px;
                font-weight: 600;
                line-height: 16px;
                color: ${NISCO.COLORS.primaryColor} !important;
                display: flex;
                align-items: center;
                gap: 2px;
            }
        `,
    },
    configureProductPlpStyles: {
        css: css`
            margin-top: 52px;
            margin-bottom: 12px;
            font-family: Poppins;
            font-size: 18px;
            font-weight: 600;
            line-height: 24px;
            color: ${NISCO.COLORS.primaryColor};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        margin-top: 60px;
                        margin-bottom: 24px;
                    `,
                    null,
                    null,
                ])}
            @media (min-width: 992px) and (max-width: 1181px) {
                margin-top: 60px !important;
                margin-bottom: 24px !important;
            }
            @media (min-width: 1517px) {
                margin-top: 60px !important;
                margin-bottom: 24px !important;
            }
        `,
    },
    wrenchIconStyles: {
        size: 16,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            background: #fff;
            padding: 16px;
            position: relative;
            padding-bottom: 20px;
            border:1px solid #E0DFE0;
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 16px;
                       
                    `,
                    css`
                        padding: 16px;
                       
                    `,
                    null,
                    css`
                        @media (min-width: 992px) and (max-width: 1180px) {
                            max-width: 97% !important;
                            flex-basis: 97% !important;
                        }
                    `,
                    null,
                ])}
        `,
    },
    compareWrapper: {
        css: css`
            div[data-test-selector^="NISProductListProductCompare"] {
                width: 100%;
                div {
                    &:last-child {
                        justify-content: flex-end;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                null,
                                null,
                                css`
                                    justify-content: flex-end;
                                `,
                                css`
                                    justify-content: flex-end;
                                `,
                                css`
                                    justify-content: flex-end;
                                `,
                            ])}
                    }
                }
            }
        `,
    },
    imageProductWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = nisProductGridCardStyles;

const NISProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
}) => {

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
            <StyledWrapper {...styles.compareWrapper}>
                <NISProductListProductCompare showCompare={showCompare} showAddToList={showAddToList} />
            </StyledWrapper>
            <StyledWrapper {...styles.imageProductWrapper}>
                <StyledWrapper>
                    {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                    <NISProductListProductImage
                        showImage={showImage}
                        showCompare={showCompare}
                        showImageBadge={showImageBadges}
                        showTextBadge={showTextBadges}
                    />
                </StyledWrapper>
                <Hidden
                    below="md"
                    css={css`
                        display: flex;
                        justify-content: flex-start;
                    `}
                >
                    {product?.isVariantParent && (
                        <Link href={product?.canonicalUrl} {...styles.moreOptionsStyles}>
                            <Icon src={NISCustonWrenchIcon} {...styles.wrenchIconStyles} />
                            {translate("Configure Product")}
                        </Link>
                    )}
                </Hidden>
                <StyledWrapper>
                    <NISProductListProductInformation
                        showAttributes={showAttributes}
                        showBrand={showBrand}
                        showPartNumbers={showPartNumbers}
                        showTitle={showTitle}
                        showImageBadges={showImageBadges}
                        maxImageBadges={maximumNumberOfImageBadges}
                        showTextBadges={showTextBadges}
                        maxTextBadges={maximumNumberOfTextBadges}
                    />
                </StyledWrapper>
                <Hidden below="md">
                    {product?.isVariantParent && (
                        <Typography as="p" {...styles.configureProductPlpStyles}>
                            {siteMessage("configureProduct_Plp_Msg")}
                        </Typography>
                    )}
                </Hidden>
                {/* <Hidden above="sm" css={css``}>
                    {product?.isVariantParent && (
                        <Typography as="p" {...styles.configureProductPlpStyles}>
                            {siteMessage("configureProduct_Plp_Msg")}
                        </Typography>
                    )}
                </Hidden> */}
            </StyledWrapper>
            <StyledWrapper>
                <NISProductListActions showPrice={showPrice} showAvailability={showAvailability} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(NISProductListProductGridCard);
